// Drift: I dag (design E2) på Supabase (del af SupabaseApi – se supabase.ts). Alt på siden kommer fra idag_overblik
// (20261030220000_idag.sql) i ét kald; afløsere for syge steamere regnes her med samme motor som fast fri-flyt (fastFriFlyt.ts),
// og Freemis forslag til en ventende booking er bookingens egen tid og steamer (som Afventer godkendelse).
import type { Api } from './api';
import type { IdagBooking, IdagData } from '../../core/idag';
import { flytForslag, type BeroertBooking } from '../../core/fastFriFlyt';
import { regler } from '../../core/motor';
import type { DagsOpgave } from '../../core/ledige';
import { lytTabeller } from './live';
import type { SbKatalog, SbKontekst } from './sbKatalog';

type Raa = IdagData & { sygOpgaver?: (DagsOpgave & { dato: string })[] };

export class SbIdag {
  constructor(private readonly c: SbKontekst, private readonly kat: SbKatalog) {}

  idagOverblik: Api['idagOverblik'] = async () => {
    const k = await this.c.kraevKlient();
    const x = (await this.c.k(this.c.sb.rpc('idag_overblik', { k }))) as Raa;
    const afventer = x.afventer && {
      ...x.afventer,
      liste: x.afventer.liste.map((b) => ({ ...b, forslag: b.steamer_id && b.start != null && b.slut != null && b.dato >= x.dato
        ? { steamer_id: b.steamer_id, navn: b.steamer ?? '', start: b.start, slut: b.slut, koersel: 0 } : null })),
    };
    const syge = await this.afloesere(x);
    const rest: Raa = { ...x };
    delete rest.sygOpgaver;
    return { ...rest, afventer, syge, tal: x.tal ?? null, manglerSteamer: x.manglerSteamer ?? null, fakturaer: x.fakturaer ?? null, afstem: x.afstem ?? null,
      penge: x.penge ?? null, prognose: x.prognose ?? null, live: x.live ?? null, workflows: x.workflows ?? null, skabeloner: x.skabeloner ?? [] };
  };

  /** Den bedste ledige afløser pr. booking hos en syg steamer (bookingerne regnes i rækkefølge, så ingen får to på samme tid). */
  private async afloesere(x: Raa): Promise<IdagData['syge']> {
    const syge = x.syge ?? [];
    if (!syge.some((s) => s.bookinger.length)) return syge;
    const [st, zoner, ind, luk] = await Promise.all([this.kat.steamere(), this.kat.zoner(), this.kat.indstillinger(), this.kat.lukkedage()]);
    const navn = new Map(st.steamere.map((s) => [s.id, s.navn]));
    const beroert = (b: IdagBooking): BeroertBooking => ({ id: b.id, dato: b.dato, start: b.start!, slut: b.slut!, steamer_id: b.steamer_id!, postnr: b.postnr, ydelse_id: b.ydelse_id, ydelse: b.ydelse, fornavn: b.kunde.split(' ')[0] ?? '' });
    const forslag = flytForslag(syge.flatMap((s) => s.bookinger.map(beroert)), {
      ...st, lukkedage: luk.map((l) => l.dato), zoner, regler: regler({ indst: ind }), opgaver: (x.sygOpgaver ?? []).map((o) => ({ ...o, id: Number(o.id) })),
    });
    const pr = new Map(forslag.map((f) => [f.id, f.forslag[0]]));
    return syge.map((s) => ({ ...s, bookinger: s.bookinger.map((b) => {
      const f = pr.get(b.id);
      return { ...b, afloeser: f ? { steamer_id: f.steamer_id, navn: navn.get(f.steamer_id) ?? f.navn, koersel: f.ekstraKoersel } : null };
    }) }));
  }

  idagLog: Api['idagLog'] = async (tekst) => { await this.c.k(this.c.sb.rpc('idag_log', { k: await this.c.kraevKlient(), p_tekst: tekst })); };
  steamerFlytSendNu: Api['steamerFlytSendNu'] = async () => (await this.c.k(this.c.sb.rpc('steamer_flyt_send_nu', { k: await this.c.kraevKlient() }))) as number;

  /** Realtime på virksomhedens rækker i tabellerne – via den fælles live-kanal (live.ts), så siden ikke åbner sin egen. */
  abonner: NonNullable<Api['abonner']> = (tabeller, fn, status) => lytTabeller(tabeller, fn, status);
}
